import React, { useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';
import './AddCandidateToSelectionModal.scss';

interface SelectionFolder {
  id: number;
  name: string;
}

interface Selection {
  id: number;
  name: string;
  folder_id?: number;
}

interface CandidateSelection {
  id: number;
  selection_id: number;
  selection_name: string;
  folder_name?: string;
}

interface AddCandidateToSelectionModalProps {
  cvId?: number;
  cvIds?: number[];
  candidateName: string;
  onClose: () => void;
  onSuccess?: (added: number, skipped: number) => void;
}

/**
 * AddCandidateToSelectionModal Component
 * 
 * Modal for adding a candidate CV to employer selections (atrankos).
 * Shows existing selections and allows adding to new ones.
 */
const AddCandidateToSelectionModal: React.FC<AddCandidateToSelectionModalProps> = ({
  cvId,
  cvIds,
  candidateName,
  onClose,
  onSuccess
}) => {
  // Resolve the list of CV IDs to add (support both single and bulk)
  const resolvedCvIds = cvIds && cvIds.length > 0 ? cvIds : cvId ? [cvId] : [];
  const [folders, setFolders] = useState<SelectionFolder[]>([]);
  const [allSelections, setAllSelections] = useState<Selection[]>([]);
  const [candidateSelections, setCandidateSelections] = useState<CandidateSelection[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [selectedSelectionId, setSelectedSelectionId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cvId, cvIds]);

  const fetchData = async () => {
    try {
      setLoadingData(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      // Fetch folders
      const foldersResponse = await fetch(`${API_URL}/employer-selections/folders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (foldersResponse.ok) {
        const foldersResult = await foldersResponse.json();
        setFolders(foldersResult.data || []);
      }

      // Fetch all selections
      const selectionsResponse = await fetch(`${API_URL}/employer-selections/selections`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (selectionsResponse.ok) {
        const selectionsResult = await selectionsResponse.json();
        setAllSelections(selectionsResult.data || []);
      }

      // TODO: Fetch existing selections for this candidate
      // For now, we'll leave it empty - this needs backend endpoint
      setCandidateSelections([]);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleAddToSelection = async () => {
    if (!selectedSelectionId || resolvedCvIds.length === 0) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const isBulk = resolvedCvIds.length > 1;
      const url = isBulk
        ? `${API_URL}/employer-selections/candidates/bulk`
        : `${API_URL}/employer-selections/candidates`;
      const body = isBulk
        ? { selection_id: selectedSelectionId, cv_ids: resolvedCvIds }
        : { selection_id: selectedSelectionId, cv_id: resolvedCvIds[0] };

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const result = await response.json();
        await fetchData();
        setSelectedFolderId(null);
        setSelectedSelectionId(null);
        setErrorMessage(null);

        const added = result.added ?? 1;
        const skipped = result.skipped ?? 0;
        setSuccessMessage(
          isBulk
            ? `Pridėta: ${added}${skipped > 0 ? `, praleista (jau yra): ${skipped}` : ''}`
            : 'Kandidatas sėkmingai pridėtas į atranką!'
        );
        setTimeout(() => setSuccessMessage(null), 3000);

        if (onSuccess) onSuccess(added, skipped);
      } else {
        const error = await response.json();
        setErrorMessage(error.message || 'Nepavyko pridėti kandidato');
        setSuccessMessage(null);
      }
    } catch (error) {
      console.error('Error adding candidate:', error);
      setErrorMessage('Įvyko klaida pridedant kandidatą');
      setSuccessMessage(null);
    } finally {
      setLoading(false);
    }
  };

  // Filter selections based on selected folder
  const filteredSelections = selectedFolderId
    ? allSelections.filter(s => s.folder_id === selectedFolderId)
    : allSelections.filter(s => !s.folder_id); // Show only standalone selections if no folder selected

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="add-candidate-modal" onClick={(e) => e.stopPropagation()}>
        <button className="btn-close-modal" onClick={onClose}>
          <X size={24} />
        </button>

        <h2 className="modal-title">Įkelti į kandidatų atrankas</h2>
        <p className="candidate-name">{candidateName}</p>

        {/* Success Message */}
        {successMessage && (
          <div className="notification success-notification">
            <Check size={20} />
            <span>{successMessage}</span>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="notification error-notification">
            <X size={20} />
            <span>{errorMessage}</span>
            <button className="btn-close-notification" onClick={() => setErrorMessage(null)}>
              ×
            </button>
          </div>
        )}

        {loadingData ? (
          <div className="loading-state">Kraunama...</div>
        ) : (
          <>
            {candidateSelections.length > 0 && (
              <div className="existing-selections">
                <p className="section-label">
                  Pasirinktas kandidatas yra įkeltas į šias atrankas:
                </p>
                <ul className="selections-list">
                  {candidateSelections.map((cs) => (
                    <li key={cs.id}>
                      • {cs.selection_name}
                      {cs.folder_name && ` (${cs.folder_name})`}
                    </li>
                  ))}
                </ul>
                <div className="divider" />
              </div>
            )}

            <div className="add-selection-form">
              <p className="section-label">
                Pasirinkite bent vieną atrankų aplanką arba atranką
              </p>

              <div className="form-group">
                <select
                  className="select-input"
                  value={selectedFolderId || ''}
                  onChange={(e) => {
                    setSelectedFolderId(e.target.value ? parseInt(e.target.value) : null);
                    setSelectedSelectionId(null); // Reset selection when folder changes
                  }}
                >
                  <option value="">Pasirinkite atrankų aplanką</option>
                  {folders.map((folder) => (
                    <option key={folder.id} value={folder.id}>
                      {folder.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <select
                  className="select-input"
                  value={selectedSelectionId || ''}
                  onChange={(e) => setSelectedSelectionId(e.target.value ? parseInt(e.target.value) : null)}
                >
                  <option value="">Pasirinkite atranką</option>
                  {filteredSelections.map((selection) => (
                    <option key={selection.id} value={selection.id}>
                      {selection.name}
                    </option>
                  ))}
                </select>
              </div>

              <button
                className="btn-add"
                onClick={handleAddToSelection}
                disabled={!selectedSelectionId || loading}
              >
                <Check size={20} />
                {loading ? 'PRIDEDAMA...' : 'ĮKELTI'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddCandidateToSelectionModal;

